import React, { useState, useEffect } from 'react';
import {
    Modal,
    Button,
    Text,
    Tag,
    BlockStack,
    InlineStack,
    Popover,
    OptionList,
    Icon,
    Spinner,
    ProgressBar,
} from '@shopify/polaris';
import {
    InfoIcon,
    CheckCircleIcon,
    AlertTriangleIcon,
    CompassIcon,
    SelectIcon,
    ArrowRightIcon,
} from '@shopify/polaris-icons';
import Language from '../../i18n/Language';
import './index.scss';

const t = Language;

/**
 * FAQ / How-To 结构化数据「设置」弹窗（可复用的受控组件）
 *
 * 该组件为展示型组件：所有业务数据与操作均通过 props 传入，
 * 组件内部只自管纯 UI 状态（页面类型下拉框的开合），方便在任意页面复用。
 *
 * @param {boolean}  open                   是否打开弹窗
 * @param {string}   type                   模块类型：'FAQ Schema' | 'How-To Schema'
 * @param {object}   config                 派生统计数据对象（即原 settingsCfg），可为 null
 * @param {Array}    pageOptions            可选页面类型 [{ label, value }]
 * @param {Array}    selectedPages          当前已选页面类型 value 数组
 * @param {number}   ulevel                 用户等级（1 表示需升级）
 * @param {boolean}  overageEnabled         是否已开启超额
 * @param {boolean}  saveLoading            保存中 loading
 * @param {string}   primaryActionText      主按钮文案（默认 'Save'，如需「保存即开启」可传 'Save & Enable'）
 * @param {Function} onSelectedPagesChange  (nextSelected: string[]) => void
 * @param {Function} onClose                关闭弹窗
 * @param {Function} onSave                 点击「Save」
 * @param {Function} onUpgrade              点击「Upgrade」（通常传 used 用量）
 * @param {Function} onEnableOverage        点击「Enable overage」
 * @param {Function} onViewHow             点击「View how.」
 */
const SchemaSettingsModal = (props) => {
    const {
        open,
        type,
        config,
        pageOptions = [],
        selectedPages = [],
        ulevel = 1,
        overageEnabled = false,
        saveLoading = false,
        primaryActionText,
        onSelectedPagesChange,
        onClose,
        onSave,
        onUpgrade,
        onEnableOverage,
        onViewHow,
    } = props;

    const [pagesPopoverActive, setPagesPopoverActive] = useState(false);

    // 弹窗关闭时重置页面类型下拉框状态（与原父组件在关闭/保存时的行为保持一致）
    useEffect(() => {
        if (!open) setPagesPopoverActive(false);
    }, [open]);

    const isFaqSettings = type === 'FAQ Schema';
    const settingsTitle = isFaqSettings
        ? t('FAQ Structured Data Settings')
        : t('How-To Structured Data Settings');
    const pageSelectorLabel = isFaqSettings
        ? t('Select pages to analyze for FAQ schema')
        : t('Select pages to analyze for How-To schema');
    const usagePercent = config && config.total > 0 ? (config.used / config.total) * 100 : 0;

    const selectedPageLabel = (val) => {
        const found = pageOptions.find((p) => p.value === val);
        return found ? found.label : val;
    };

    const handleTogglePageOption = (selected) => {
        onSelectedPagesChange && onSelectedPagesChange(selected);
    };

    const handleRemovePageTag = (value) => {
        onSelectedPagesChange && onSelectedPagesChange(selectedPages.filter((v) => v !== value));
    };

    const handleClose = () => {
        if (saveLoading) return;
        setPagesPopoverActive(false);
        onClose && onClose();
    };

    // 「升级 / 开启超额」按钮（根据用户等级展示不同入口）
    const renderUpgradeOrOverage = () => (
        ulevel === 1 ? (
            <Button variant='plain' onClick={() => onUpgrade && onUpgrade(config?.used || 0)}>
                <span className='enable-overage-button'>
                    <span>{t('Upgrade')}</span>
                    <span><Icon source={ArrowRightIcon} /></span>
                </span>
            </Button>
        ) : (
            <Button variant='plain' onClick={() => onEnableOverage && onEnableOverage()}>
                <span className='enable-overage-button'>
                    <span>{t('Enable overage')}</span>
                    <span><Icon source={ArrowRightIcon} /></span>
                </span>
            </Button>
        )
    );

    return (
        <Modal
            title={settingsTitle}
            open={open}
            onClose={handleClose}
            primaryAction={{
                content: primaryActionText || t('Save'),
                onAction: () => onSave && onSave(),
                loading: saveLoading,
                disabled: selectedPages.length === 0,
            }}
            secondaryActions={[
                {
                    content: t('Close'),
                    onAction: handleClose,
                    disabled: saveLoading,
                },
            ]}
        >
            <Modal.Section>
                <BlockStack gap="400">
                    <div className="schema-settings-credits">
                        <div className="schema-settings-credits-main">
                            <BlockStack gap="200">
                                <InlineStack align="space-between" blockAlign="center" wrap={false}>
                                    <Text as="p" variant="bodyMd" fontWeight="medium">{t('AI Credits')}</Text>
                                    <Text as="p" variant="bodySm" tone="subdued">
                                        {t('Resets {{v1}}', config ? config.resetDate : '')}
                                    </Text>
                                </InlineStack>
                                <BlockStack gap="100">
                                    <InlineStack gap="100" blockAlign="baseline" wrap={false}>
                                        <Text as="p" variant="headingLg" fontWeight="bold">
                                            {config ? config.used : 0}
                                        </Text>
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            {t('of {{v1}} used this month', config ? config.total : 0)}
                                        </Text>
                                    </InlineStack>
                                    <ProgressBar
                                        progress={usagePercent}
                                        size="small"
                                        tone={usagePercent >= 100 ? 'critical' : 'highlight'}
                                    />
                                    <div className="schema-settings-credits-foot">
                                        {(() => {
                                            if (!config) return null;
                                            const { analyzeState, analyzed, schemaCount, pendingPages, pendingCredits } = config;

                                            // State 3 — 开关开启 + 正在分析
                                            if (analyzeState === 3) {
                                                return (
                                                    <InlineStack gap="100" blockAlign="center" wrap={false}>
                                                        <Spinner size='small' />
                                                        <Text as="p" variant="bodySm" tone="subdued">
                                                            {t('Analyzing pages') + '...'}
                                                        </Text>
                                                    </InlineStack>
                                                );
                                            }

                                            // State 1 — 所有页面都已分析 (pending === 0)
                                            if (analyzeState === 1) {
                                                if (analyzed > 0) {
                                                    return (
                                                        <InlineStack gap="100" blockAlign="center" wrap={false}>
                                                            <Icon source={CheckCircleIcon} tone="success" />
                                                            <Text as="p" variant="bodySm" tone="subdued">
                                                                {t('All {{v1}} pages analyzed · {{v2}} pages with {{v3}} schema',
                                                                    analyzed,
                                                                    schemaCount,
                                                                    isFaqSettings ? t('FAQ') : t('How-To'))}
                                                            </Text>
                                                        </InlineStack>
                                                    );
                                                }
                                                // 所选类型下没有页面
                                                return (
                                                    <Text as="p" variant="bodySm" tone="subdued">
                                                        {t('Pages to analyze: {{v1}} · ~{{v2}} credits', 0, 0)}
                                                    </Text>
                                                );
                                            }

                                            // State 2 — 开关开启 + 额度耗尽 + 有 pending
                                            if (analyzeState === 2) {
                                                return (
                                                    <InlineStack gap="100" blockAlign="center" wrap={false}>
                                                        <Icon source={AlertTriangleIcon} tone="critical" />
                                                        <Text as="p" variant="bodySm" tone="subdued">
                                                            {t('{{v1}} pages pending', pendingPages)}
                                                        </Text>
                                                    </InlineStack>
                                                );
                                            }

                                            // State 0 — 开关关闭 + 有 pending
                                            return (
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    {t('Pages to analyze: {{v1}} · ~{{v2}} credits', pendingPages, pendingCredits)}
                                                </Text>
                                            );
                                        })()}

                                        {(() => {
                                            if (!config) return null;
                                            const { analyzeState } = config;
                                            if (analyzeState === 2) {
                                                return renderUpgradeOrOverage();
                                            }
                                        })()}
                                    </div>
                                </BlockStack>
                            </BlockStack>
                        </div>
                        {/* 超额提示底部条 — 仅当存在 pending 且 credits 不足时展示 */}
                        {config?.analyzeState !== 2 && config && config.pendingPages > 0 && config.creditsInsufficient && (
                            <div className="schema-settings-credits-overage">
                                {overageEnabled ? (
                                    <InlineStack gap="100" blockAlign="center" wrap={false}>
                                        <Icon source={CheckCircleIcon} tone="success" />
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            {t('Overage on • Extra ~{{v1}} credits will be billed (~${{v2}})',
                                                config.extraCreditsNeeded,
                                                config.billingPrice)}
                                        </Text>
                                    </InlineStack>
                                ) : (
                                    <>
                                        <InlineStack gap="100" blockAlign="center" wrap={false}>
                                            <Icon source={InfoIcon} tone="base" />
                                            <Text as="p" variant="bodySm" tone="subdued">
                                                {t('Overage off · {{v1}} pages will pause when credits run out',
                                                    config.pagesWillPause)}
                                            </Text>
                                        </InlineStack>
                                        {renderUpgradeOrOverage()}
                                    </>
                                )}
                            </div>
                        )}
                    </div>

                    <BlockStack gap="100">
                        <Text as="p" variant="bodyMd" fontWeight="medium">{pageSelectorLabel}</Text>
                        <Popover
                            fullWidth
                            active={pagesPopoverActive}
                            preferredAlignment="left"
                            activator={(
                                <div
                                    className="schema-settings-page-select"
                                    onClick={() => setPagesPopoverActive(!pagesPopoverActive)}
                                >
                                    <div className="schema-settings-page-tags">
                                        {selectedPages.length === 0 ? (
                                            <Text as="span" variant="bodySm" tone="subdued">
                                                {t('Select page types')}
                                            </Text>
                                        ) : (
                                            selectedPages.map((val) => (
                                                <span key={val} onClick={(e) => e.stopPropagation()}>
                                                    <Tag onRemove={() => handleRemovePageTag(val)}>
                                                        {selectedPageLabel(val)}
                                                    </Tag>
                                                </span>
                                            ))
                                        )}
                                    </div>
                                    <span className="schema-settings-page-select-icon">
                                        <Icon source={SelectIcon} tone="base" />
                                    </span>
                                </div>
                            )}
                            onClose={() => setPagesPopoverActive(false)}
                        >
                            <OptionList
                                allowMultiple
                                onChange={handleTogglePageOption}
                                options={pageOptions}
                                selected={selectedPages}
                            />
                        </Popover>
                    </BlockStack>

                    <div className="schema-settings-howitworks">
                        <InlineStack gap="100" blockAlign="start" wrap={false}>
                            <span className="schema-settings-howitworks-icon">
                                <Icon source={CompassIcon} />
                            </span>
                            <Text as="p" variant="bodyMd" fontWeight="semibold">
                                {t('How it works')}
                            </Text>
                        </InlineStack>
                        <ul className="schema-settings-howitworks-list">
                            {isFaqSettings ? (
                                <>
                                    <li>{t('Selected pages are automatically analyzed for FAQ content based on page descriptions (such as product descriptions and blog content).')}</li>
                                    <li>{t('Please ensure your pages already contain clear FAQ content before enabling analysis. Pages without detectable FAQ content cannot generate FAQ schema, but credits will still be consumed (10 analyzed pages = 1 credit).')}</li>
                                    <li>{t('Once enabled, newly published pages will be analyzed automatically.')}</li>
                                    <li>
                                        {t("FAQ schema is only generated once automatically. If an existing page is updated later, use the \"Regenerate\" button on that page to generate a new version.")}
                                        &nbsp;
                                        <Button variant='plain' onClick={() => onViewHow && onViewHow()}>{t("View how.")}</Button>
                                    </li>
                                </>
                            ) : (
                                <>
                                    <li>{t('Selected pages are automatically analyzed for How-To content based on page descriptions, such as product descriptions and blog content.')}</li>
                                    <li>{t('Please ensure your pages already contain clear step-by-step How-To content before enabling analysis. Pages without detectable How-To content cannot generate How-To structured data, but credits will still be consumed (10 analyzed pages = 1 credit).')}</li>
                                    <li>{t('Once enabled, newly published pages will be analyzed automatically.')}</li>
                                    <li>
                                        {t("How-To schema is generated automatically only once per page. If a page is updated later, use the \"Regenerate\" button on that page to generate a new version.")}
                                        &nbsp;
                                        <Button variant='plain' onClick={() => onViewHow && onViewHow()}>{t("View how.")}</Button>
                                    </li>
                                </>
                            )}
                        </ul>
                    </div>
                </BlockStack>
            </Modal.Section>
        </Modal>
    );
};

export default SchemaSettingsModal;
